import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import PageHeader from '../components/ui/PageHeader';
import Pagination from '../components/ui/Pagination';
import { EmptyState, ErrorState, TableSkeleton } from '../components/ui/States';
import ScansTable from '../components/scans/ScansTable';
import useScans from '../components/scans/useScans';
import { sortNewestFirst, STATUSES, toRowView } from '../components/scans/scanAdapter';
import { detailPath, MAX_SEARCH_LENGTH, MIN_SEARCH_LENGTH, PAGE_SIZES } from '../components/scans/scansParams';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '…');

const CHIPS = [...STATUSES.map((s) => ({ key: s.key, label: s.chip })), { key: 'all', label: 'All' }];

function errorView(error) {
  if (error?.status === 403) return { icon: 'lock', title: 'Admin access required', retry: false };
  if (error?.status === 429) return { icon: 'hourglass_top', title: 'Too many requests, try again shortly', retry: true, retryAfter: 5 };
  return { icon: 'cloud_off', title: "Couldn't load scans", description: error?.message, retry: true };
}

function FilterChip({ label, value, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-secondary-container/40 text-xs text-on-secondary-container">
      {label}: <span className="font-data-mono font-semibold truncate max-w-[180px]">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label.toLowerCase()} filter`}
        className="p-0.5 rounded-full hover:bg-secondary-container outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
      >
        <Icon name="close" size={14} />
      </button>
    </span>
  );
}

/** Scan Details: the receipt scan review queue and history. */
export default function ScansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { params, setParams, searchInput, setSearchInput, clearFilters, scans, statusCounts, pagination, isInitialLoading, isRefreshing, error, refetch } =
    useScans();

  const rows = useMemo(() => sortNewestFirst(scans).map(toRowView), [scans]);
  const total = pagination?.totalItems ?? 0;
  const from = total ? (params.page - 1) * params.limit + 1 : 0;
  const to = Math.min(params.page * params.limit, total);
  const hasFilters = Boolean(params.search || params.userId || params.placeId || params.status !== 'pending');
  const tooShort = searchInput.trim().length > 0 && searchInput.trim().length < MIN_SEARCH_LENGTH;

  let body;
  if (error && !isInitialLoading) {
    const view = errorView(error);
    body = (
      <ErrorState
        icon={view.icon}
        title={view.title}
        description={view.description}
        retryAfter={view.retryAfter}
        onRetry={view.retry ? () => refetch() : undefined}
      />
    );
  } else if (isInitialLoading) {
    body = <TableSkeleton rows={6} message="Loading scans…" />;
  } else if (pagination && rows.length === 0) {
    body = (
      <EmptyState
        tone={params.status === 'pending' && !hasFilters ? 'ok' : 'neutral'}
        icon={params.status === 'pending' && !hasFilters ? 'task_alt' : 'receipt_long'}
        title="No scans match these filters"
        description={params.status === 'pending' ? 'Nothing is waiting for review right now.' : 'Try another status or search.'}
        action={
          hasFilters && (
            <Button variant="secondary" icon="filter_alt_off" onClick={clearFilters}>
              Clear filters
            </Button>
          )
        }
      />
    );
  } else {
    body = <ScansTable rows={rows} refreshing={isRefreshing} onOpen={(id) => navigate(detailPath(id, location.search))} />;
  }

  return (
    <>
      <PageHeader
        title="Scan Details"
        badge={
          statusCounts && (
            <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-status-pending-bg border border-status-pending-border text-xs font-semibold text-status-pending-text">
              <span className="w-1.5 h-1.5 rounded-full bg-status-pending-dot" aria-hidden="true" />
              <span className="font-data-mono">{fmt(statusCounts.pending)}</span> pending review
            </span>
          )
        }
        description="Review receipt scans submitted by drivers, correct what OCR misread, and approve prices onto the live station map."
      />

      <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-3 flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1" role="search">
            <label htmlFor="scans-search" className="sr-only">
              Search scans
            </label>
            <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" />
            <input
              id="scans-search"
              type="text"
              autoComplete="off"
              maxLength={MAX_SEARCH_LENGTH}
              placeholder="Search by scan ID, user, email, station, place ID or receipt number…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-describedby={tooShort ? 'scans-search-hint' : undefined}
              className="w-full h-10 pl-9 pr-9 text-[13px] bg-surface-container-lowest border border-outline-variant/50 rounded-lg text-on-surface placeholder:text-on-surface-variant/60 outline-none transition focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-on-surface-variant hover:text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
              >
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
          <div role="radiogroup" aria-label="Filter by status" className="flex gap-1.5 overflow-x-auto">
            {CHIPS.map((chip) => {
              const selected = params.status === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => !selected && setParams({ status: chip.key })}
                  className={`shrink-0 h-9 px-3 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot focus-visible:ring-offset-1 ${
                    selected
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                  }`}
                >
                  {chip.label}{' '}
                  <span className={`font-data-mono text-xs ${selected ? 'text-on-primary-container' : ''}`}>{fmt(statusCounts?.[chip.key])}</span>
                </button>
              );
            })}
          </div>
        </div>
        {tooShort && (
          <p id="scans-search-hint" className="text-[11px] text-on-surface-variant">
            Type at least {MIN_SEARCH_LENGTH} characters
          </p>
        )}
        {(params.userId || params.placeId) && (
          <div className="flex flex-wrap gap-2" aria-label="Active filters">
            {params.userId && <FilterChip label="User" value={params.userId} onRemove={() => setParams({ userId: '' })} />}
            {params.placeId && <FilterChip label="Station" value={params.placeId} onRemove={() => setParams({ placeId: '' })} />}
          </div>
        )}
      </section>

      <section className="relative bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs overflow-hidden">
        <p className="sr-only" role="status" aria-live="polite">
          {isInitialLoading || isRefreshing || !pagination ? '' : `${total} ${total === 1 ? 'scan' : 'scans'} found`}
        </p>
        {isRefreshing && (
          <div className="absolute top-2 right-3 z-20 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-surface-container-lowest/90 shadow-xs text-[11px] text-on-surface-variant">
            <Icon name="progress_activity" size={14} className="animate-spin text-secondary" />
            Loading…
          </div>
        )}
        {body}
        {!error && pagination && total > 0 && (
          <Pagination
            page={params.page}
            pageSize={params.limit}
            total={total}
            onPageChange={(page) => setParams({ page })}
            pageSizeOptions={PAGE_SIZES}
            onPageSizeChange={(limit) => setParams({ limit })}
            disabled={isRefreshing}
            summary={
              <span>
                Showing <strong className="font-data-mono text-on-surface">{fmt(from)}</strong> to <strong className="font-data-mono text-on-surface">{fmt(to)}</strong> of{' '}
                <strong className="font-data-mono text-on-surface">{fmt(total)}</strong> {total === 1 ? 'scan' : 'scans'}
              </span>
            }
          />
        )}
      </section>
    </>
  );
}
